/**
 * Connection-aware scheduling so LCP work (hero image) isn't competing
 * with gallery JS, extra images, or non-critical API calls on slow links.
 */

export function isSlowConnection() {
  if (typeof navigator === "undefined") return false
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (!conn) return false
    if (conn.saveData) return true
    const type = conn.effectiveType
    return type === "slow-2g" || type === "2g" || type === "3g"
  } catch {
    return false
  }
}

/**
 * Run callback after the browser is idle, with a longer timeout on slow networks.
 * Returns a cancel function.
 */
export function scheduleDeferred(callback, { slowDelay = 2500, fastDelay = 600 } = {}) {
  if (typeof window === "undefined") return () => {}

  const delay = isSlowConnection() ? slowDelay : fastDelay
  let idleId = null
  let timeoutId = null
  let rafId = null
  let cancelled = false

  const run = () => {
    if (!cancelled) callback()
  }

  if (delay <= 0) {
    rafId = window.requestAnimationFrame(run)
    return () => {
      cancelled = true
      if (rafId != null) window.cancelAnimationFrame(rafId)
    }
  }

  if (typeof window.requestIdleCallback === "function") {
    idleId = window.requestIdleCallback(run, { timeout: delay })
    return () => {
      cancelled = true
      window.cancelIdleCallback(idleId)
    }
  }

  timeoutId = window.setTimeout(run, delay)
  return () => {
    cancelled = true
    window.clearTimeout(timeoutId)
  }
}
